<script lang="ts" setup>
import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Vector3 } from '@babylonjs/core/Maths/math';
import CollapseFieldset from '@/components/CollapseFieldset.vue';

import type { Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifestHotspot, IManifestXYZPosition } from '@/types/IManifest';
import type { HotspotPicker, Hotspots } from '@/viewerExtensions';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const hotspot = inject('hotspot') as Ref<IManifestHotspot>;
const activeTarget = ref(0);
const hotspotPickerExtension = viewer.value?.extensions.get('HotspotPicker') as Extension<HotspotPicker>;
const hotspotsExtension = viewer.value?.extensions.get('Hotspots') as Extension<Hotspots>;
type HotspotPositionAxis = keyof IManifestXYZPosition;

const addNewTarget = () => {
  const newHotspotTargetPosition = { x: 0, y: 0, z: 0 } as IManifestXYZPosition;
  const newHotspotTargetPositionVector3 = new Vector3(newHotspotTargetPosition.x, newHotspotTargetPosition.y, newHotspotTargetPosition.z);

  hotspot.value.position.push(newHotspotTargetPosition);

  hotspotsExtension.createHotspotTarget(newHotspotTargetPosition, hotspot.value.hotspotName);
  hotspotsExtension.showHotspot(hotspot.value.hotspotName);
  hotspotPickerExtension.createHotspotPlacer(newHotspotTargetPositionVector3);

  activeTarget.value = hotspot.value.position.length - 1;
};

const removeHotspotTarget = (index: number) => {
  if (index === 0) return;

  hotspot.value.position.splice(index, 1);

  hotspotsExtension.destroyHotspotTarget(hotspot.value.hotspotName, index);
  hotspotPickerExtension.destroyHotspotPlacer(index);

  activeTarget.value = 0;
};

const setActiveTarget = (newTarget: number) => {
  if (activeTarget.value === newTarget) return;

  activeTarget.value = newTarget;
};

const changeActiveTarget = (newTarget: number) => {
  if (activeTarget.value === newTarget) activeTarget.value = -1;
  else activeTarget.value = newTarget;
};

const activeTargetWatcher = (newValue: number) => {
  if (newValue < 0) return;

  hotspotPickerExtension.attachGizmoToHotspot(newValue);
};

const formatHotspotTargetCoordinate = (value: number) => {
  return Number.isFinite(value) ? value.toFixed(6) : '0.000000';
};

const syncHotspotTargetPosition = (targetIndex: number) => {
  const position = hotspot.value.position[targetIndex];

  if (!position || !viewer.value) return;

  const positionVector = new Vector3(position.x, position.y, position.z);

  hotspotPickerExtension.moveHotspotPlacer(targetIndex, positionVector);
  hotspotsExtension.moveHotspotTarget(hotspot.value.hotspotName, targetIndex, position);

  viewer.value.renderNextFrame += 1;
};

const updateHotspotTargetCoordinate = (targetIndex: number, axis: HotspotPositionAxis, event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  const coordinateValue = inputElement.value === '' ? Number.NaN : Number(inputElement.value);
  const currentPosition = hotspot.value.position[targetIndex];

  if (!currentPosition) return;

  if (!Number.isFinite(coordinateValue)) {
    inputElement.value = formatHotspotTargetCoordinate(currentPosition[axis]);
    return;
  }

  hotspot.value.position[targetIndex] = {
    ...currentPosition,
    [axis]: coordinateValue,
  };

  setActiveTarget(targetIndex);
  syncHotspotTargetPosition(targetIndex);
};

const mountHandler = () => {
  if (!viewer.value) return;

  hotspotPickerExtension.destroyAllHotspotPlacers();

  for (const position of hotspot.value.position) {
    hotspotPickerExtension.createHotspotPlacer(new Vector3(position.x, position.y, position.z));
  }

  hotspotPickerExtension.detachGizmo();
  hotspotPickerExtension.attachGizmoToHotspot(activeTarget.value);
};

const unmountHandler = () => {
  if (!viewer.value) return;

  hotspotPickerExtension.destroyAllHotspotPlacers();
  hotspotPickerExtension.detachGizmo();
};

onMounted(mountHandler);
onBeforeUnmount(unmountHandler);

watch(activeTarget, activeTargetWatcher);
watch(hotspot, mountHandler);
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Targets
    </template>

    <div
      v-for="(target, index) in hotspot.position"
      :key="`${hotspot.hotspotName}-target-${index}`"
      :class="{ 'outline outline-blue-400': activeTarget === index }"
      class="cursor-pointer"
      @click="changeActiveTarget(index)"
    >
      <CollapseFieldset :enable-collapse="false">
        <template #legend>
          Target {{ index + 1 }}
        </template>

        <div class="flex flex-col gap-1">
          <label class="flex flex-row items-center font-mono px-1 bg-black/[0.05] rounded-sm">
            <span class="font-mono text-sm leading-none">x:</span>
            <input
              class="font-mono text-sm leading-none ml-auto border bg-white px-1 py-0.5 w-32 text-right"
              type="number"
              step="0.000001"
              :value="formatHotspotTargetCoordinate(target.x)"
              @click.stop
              @focus="setActiveTarget(index)"
              @change.stop="updateHotspotTargetCoordinate(index, 'x', $event)"
            >
          </label>
          <label class="flex flex-row items-center font-mono px-1 bg-black/[0.05] rounded-sm">
            <span class="font-mono text-sm leading-none">y:</span>
            <input
              class="font-mono text-sm leading-none ml-auto border bg-white px-1 py-0.5 w-32 text-right"
              type="number"
              step="0.000001"
              :value="formatHotspotTargetCoordinate(target.y)"
              @click.stop
              @focus="setActiveTarget(index)"
              @change.stop="updateHotspotTargetCoordinate(index, 'y', $event)"
            >
          </label>
          <label class="flex flex-row items-center font-mono px-1 bg-black/[0.05] rounded-sm">
            <span class="font-mono text-sm leading-none">z:</span>
            <input
              class="font-mono text-sm leading-none ml-auto border bg-white px-1 py-0.5 w-32 text-right"
              type="number"
              step="0.000001"
              :value="formatHotspotTargetCoordinate(target.z)"
              @click.stop
              @focus="setActiveTarget(index)"
              @change.stop="updateHotspotTargetCoordinate(index, 'z', $event)"
            >
          </label>
        </div>

        <button
          v-if="index !== 0"
          type="button"
          class="mt-1 text-xs cursor-pointer w-full bg-red-700 text-white rounded-sm px-2 py-1"
          @click.stop="removeHotspotTarget(index)"
        >
          Remove Target {{ index + 1 }}
        </button>
      </CollapseFieldset>
    </div>
    <button type="button" class="mt-1 text-xs cursor-pointer w-full bg-blue-400 text-white rounded-sm px-2 py-1" @click="addNewTarget">
      Add New Target
    </button>
  </CollapseFieldset>
</template>
